// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import type { Session } from '@supabase/supabase-js';
import type { Profile } from '../../types';
import type { AdminSubscription } from '../../types/admin';
import { format } from 'date-fns';
import { AdminPagination } from './AdminPagination';
import { AdminSearchBar } from './AdminSearchBar';
import { AdminConfirmDialog } from './AdminConfirmDialog';
import { AdminQuickFilters } from './AdminQuickFilters';
import { AdminLoadingSkeleton } from './AdminLoadingSkeleton';
import { AdminDateRangeFilter } from './AdminDateRangeFilter';
import { AdminColumnToggle } from './AdminColumnToggle';
import { AdminImportDialog } from './AdminImportDialog';
import { useAdminToast } from './useAdminToast';
import { useDebounce } from './useDebounce';
import { exportToCSV, exportToJSON } from './AdminExportUtils';

interface AdminSubscriptionsProps {
  session: Session;
  profile: Profile | null;
}

export const AdminSubscriptions: React.FC<AdminSubscriptionsProps> = ({ session, profile }) => {
  const [subscriptions, setSubscriptions] = useState<AdminSubscription[]>([]);
  const [allSubscriptions, setAllSubscriptions] = useState<AdminSubscription[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [activeQuickFilter, setActiveQuickFilter] = useState<string | undefined>();
  const [dateRange, setDateRange] = useState<{ start: Date | null; end: Date | null }>({ start: null, end: null });
  const [visibleColumns, setVisibleColumns] = useState<string[]>(['select', 'user', 'topic', 'email_enabled', 'created_at']);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [updatingEmailEnabled, setUpdatingEmailEnabled] = useState<string | null>(null);
  const { showSuccess, showError } = useAdminToast();
  
  // Debounce search input
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  useEffect(() => {
    let filtered = allSubscriptions;
    
    // Apply quick filters
    if (activeQuickFilter === 'email_enabled') {
      filtered = filtered.filter((sub) => sub.email_enabled);
    } else if (activeQuickFilter === 'email_disabled') {
      filtered = filtered.filter((sub) => !sub.email_enabled);
    } else if (activeQuickFilter === 'recent') {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      filtered = filtered.filter((sub) => {
        if (!sub.created_at) return false;
        const subDate = new Date(sub.created_at);
        return !isNaN(subDate.getTime()) && subDate >= sevenDaysAgo;
      });
    }
    
    // Apply date range filter
    if (dateRange.start && dateRange.end) {
      filtered = filtered.filter((sub) => {
        if (!sub.created_at) return false;
        const subDate = new Date(sub.created_at);
        return !isNaN(subDate.getTime()) && subDate >= dateRange.start! && subDate <= dateRange.end!;
      });
    }
    
    // Apply search filter
    if (debouncedSearchQuery) {
      const query = debouncedSearchQuery.toLowerCase();
      filtered = filtered.filter((sub) => {
        const userName = sub.profiles
          ? `${sub.profiles.first_name || ''} ${sub.profiles.last_name || ''}`.trim().toLowerCase()
          : '';
        const topicName = sub.law_topics?.name?.toLowerCase() || '';
        return userName.includes(query) || topicName.includes(query);
      });
    }
    
    setSubscriptions(filtered);
    setCurrentPage(1); // Reset to first page when filtering
  }, [debouncedSearchQuery, allSubscriptions, activeQuickFilter, dateRange]);

  const quickFilters = [
    {
      id: 'all',
      label: 'All',
      icon: 'subscriptions',
      onClick: () => setActiveQuickFilter(undefined),
      count: allSubscriptions.length,
    },
    {
      id: 'email_enabled',
      label: 'Email Enabled',
      icon: 'mail',
      onClick: () => setActiveQuickFilter('email_enabled'),
      count: allSubscriptions.filter((sub) => sub.email_enabled).length,
    },
    {
      id: 'email_disabled',
      label: 'Email Disabled',
      icon: 'mail_outline',
      onClick: () => setActiveQuickFilter('email_disabled'),
      count: allSubscriptions.filter((sub) => !sub.email_enabled).length,
    },
    {
      id: 'recent',
      label: 'Recent (7d)',
      icon: 'schedule',
      onClick: () => setActiveQuickFilter('recent'),
      count: allSubscriptions.filter((sub) => {
        if (!sub.created_at) return false;
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        const subDate = new Date(sub.created_at);
        return !isNaN(subDate.getTime()) && subDate >= sevenDaysAgo;
      }).length,
    },
  ];

  const handleExportCSV = () => {
    const dataToExport = selectedRows.length > 0 
      ? subscriptions.filter((sub) => selectedRows.includes(sub.id))
      : subscriptions;
    
    const csvData = dataToExport.map((sub) => ({
      User: sub.profiles
        ? `${sub.profiles.first_name || ''} ${sub.profiles.last_name || ''}`.trim() || 'Unknown'
        : 'Unknown',
      Topic: sub.law_topics?.name || 'Unknown',
      'Email Enabled': sub.email_enabled ? 'Yes' : 'No',
      'Created At': sub.created_at ? format(new Date(sub.created_at), 'yyyy-MM-dd HH:mm:ss') : 'N/A',
    }));
    exportToCSV(csvData, selectedRows.length > 0 ? 'selected_subscriptions' : 'subscriptions');
    showSuccess(
      selectedRows.length > 0
        ? `${selectedRows.length} selected subscription(s) exported to CSV successfully`
        : 'Subscriptions exported to CSV successfully'
    );
  };

  const handleExportJSON = () => {
    const dataToExport = selectedRows.length > 0 
      ? subscriptions.filter((sub) => selectedRows.includes(sub.id))
      : subscriptions;
    
    exportToJSON(dataToExport, selectedRows.length > 0 ? 'selected_subscriptions' : 'subscriptions');
    showSuccess(
      selectedRows.length > 0
        ? `${selectedRows.length} selected subscription(s) exported to JSON successfully`
        : 'Subscriptions exported to JSON successfully'
    );
  };

  const columnDefinitions = [
    {
      id: 'select',
      label: 'Select',
      defaultVisible: true,
    },
    {
      id: 'user',
      label: 'User',
      defaultVisible: true,
    },
    {
      id: 'topic',
      label: 'Topic',
      defaultVisible: true,
    },
    {
      id: 'email_enabled',
      label: 'Email Enabled',
      defaultVisible: true,
    },
    {
      id: 'created_at',
      label: 'Created',
      defaultVisible: true,
    },
  ];

  const handleToggleColumn = (columnId: string) => {
    setVisibleColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  };

  const handleToggleAllColumns = (visible: boolean) => {
    if (visible) {
      setVisibleColumns(columnDefinitions.map((col) => col.id));
    } else {
      setVisibleColumns(['select']); // Keep select column always visible
    }
  };

  const handleImport = async (data: any[]) => {
    try {
      // Transform imported data to match subscription structure
      const subscriptionsToImport = [];
      
      for (const row of data) {
        // Get user_id from email or user name
        const userEmail = row.email || row['Email'] || row['User Email'] || '';
        const userName = row.user || row['User'] || row['User Name'] || '';
        
        // Get topic_id from topic name or slug
        const topicName = row.topic || row['Topic'] || row['Topic Name'] || '';
        const topicSlug = row['Topic Slug'] || row.topic_slug || '';
        
        let userId = row.user_id || row['User ID'] || null;
        let topicId = row.topic_id || row['Topic ID'] || null;
        
        // Resolve user_id if not provided
        if (!userId && userEmail) {
          const { data: userData } = await supabase
            .from('profiles')
            .select('id')
            .eq('email', userEmail)
            .single();
          if (userData) userId = userData.id;
        }
        
        // Resolve topic_id if not provided
        if (!topicId && topicName) {
          const { data: topicData } = await supabase
            .from('law_topics')
            .select('id')
            .or(`name.ilike.%${topicName}%,slug.eq.${topicSlug}`)
            .single();
          if (topicData) topicId = topicData.id;
        }
        
        if (userId && topicId) {
          subscriptionsToImport.push({
            user_id: userId,
            topic_id: topicId,
            email_enabled: row.email_enabled !== undefined 
              ? row.email_enabled 
              : row['Email Enabled'] !== undefined 
                ? row['Email Enabled'].toString().toLowerCase() === 'yes' || row['Email Enabled'].toString().toLowerCase() === 'true'
                : true,
          });
        }
      }

      if (subscriptionsToImport.length === 0) {
        throw new Error('No valid subscriptions to import (check user emails and topic names)');
      }

      // Import subscriptions (upsert to handle duplicates)
      const { error } = await supabase.from('user_topic_subscriptions').upsert(
        subscriptionsToImport,
        {
          onConflict: 'user_id,topic_id',
          ignoreDuplicates: false,
        }
      );

      if (error) throw error;
      await loadSubscriptions();
      showSuccess(`Successfully imported ${subscriptionsToImport.length} subscription(s)`);
    } catch (err: any) {
      console.error('Error importing subscriptions:', err);
      throw new Error(err.message || 'Failed to import subscriptions');
    }
  };

  // Sample import data format - Replace with your actual data when importing
  const sampleImportData = [
    {
      'User Email': 'example@domain.com', // Replace with actual user email
      'Topic Name': 'Corporate Law', // Must match existing topic name
      'Email Enabled': 'Yes', // 'Yes' or 'No'
    },
    {
      'User Email': 'another@domain.com', // Replace with actual user email
      'Topic Name': 'Employment Law', // Must match existing topic name
      'Email Enabled': 'Yes', // 'Yes' or 'No'
    },
  ];

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) return;
    setShowDeleteDialog(true);
  };

  const confirmBulkDelete = async () => {
    try {
      const { error } = await supabase
        .from('user_topic_subscriptions')
        .delete()
        .in('id', selectedRows);

      if (error) throw error;
      await loadSubscriptions();
      setSelectedRows([]);
      showSuccess(`${selectedRows.length} subscription(s) deleted successfully`);
    } catch (err: any) {
      console.error('Error bulk deleting subscriptions:', err);
      showError(err.message || 'Failed to delete subscriptions');
    } finally {
      setShowDeleteDialog(false);
    }
  };

  const handleToggleEmailEnabled = async (subscriptionId: string, currentValue: boolean) => {
    setUpdatingEmailEnabled(subscriptionId);
    try {
      const { error } = await supabase
        .from('user_topic_subscriptions')
        .update({ email_enabled: !currentValue })
        .eq('id', subscriptionId);

      if (error) throw error;
      await loadSubscriptions();
      showSuccess(`Email notifications ${!currentValue ? 'enabled' : 'disabled'} successfully`);
    } catch (err: any) {
      console.error('Error toggling email enabled:', err);
      showError(err.message || 'Failed to update email notification setting');
    } finally {
      setUpdatingEmailEnabled(null);
    }
  };

  useEffect(() => {
    loadSubscriptions();

    // Set up real-time subscription
    const channel = supabase
      .channel('admin-subscriptions-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_topic_subscriptions',
        },
        () => {
          loadSubscriptions();
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, []);

  const loadSubscriptions = async () => {
    setLoading(true);
    try {
      // Load subscriptions with law_topics relationship
      // Note: user_id references auth.users, not profiles, so we need to fetch profiles separately
      const { data: subscriptionsData, error: subscriptionsError } = await supabase
        .from('user_topic_subscriptions')
        .select(
          `
          *,
          law_topics:topic_id (id, name, slug)
        `,
          { count: 'exact' }
        )
        .order('created_at', { ascending: false });

      if (subscriptionsError) throw subscriptionsError;

      // Get unique user IDs
      const userIds = [...new Set((subscriptionsData || []).map(sub => sub.user_id))];
      
      // Fetch profiles for all users
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name')
        .in('id', userIds);

      if (profilesError) {
        console.warn('Error loading profiles:', profilesError);
        // Continue without profiles - subscriptions will still load
      }

      // Create a map of user_id -> profile for quick lookup
      const profilesMap = new Map(
        (profilesData || []).map(profile => [profile.id, profile])
      );

      // Merge profiles data into subscriptions
      const subscriptionsWithProfiles = (subscriptionsData || []).map(sub => ({
        ...sub,
        profiles: profilesMap.get(sub.user_id) || null,
      }));

      setAllSubscriptions(subscriptionsWithProfiles);
      setTotalCount(subscriptionsWithProfiles.length);
    } catch (err: any) {
      console.error('Error loading subscriptions:', err);
      showError(err.message || 'Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="mb-6">
          <div className="h-8 bg-luna-secondary/20 rounded animate-pulse w-64 mb-2" />
          <div className="h-4 bg-luna-secondary/10 rounded animate-pulse w-96" />
        </div>
        <AdminLoadingSkeleton rows={10} columns={5} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-3xl font-black text-luna-text-light dark:text-luna-text-dark mb-2">
              Subscriptions Management
            </h1>
            <p className="text-luna-text-light/70 dark:text-luna-text-dark/70">
              View all user subscriptions to law topics ({subscriptions.length} of {totalCount})
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 flex-wrap">
            {selectedRows.length > 0 && (
              <button
                onClick={handleBulkDelete}
                className="w-full sm:w-auto px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 min-h-[44px] touch-manipulation"
              >
                Delete Selected ({selectedRows.length})
              </button>
            )}
            <button
              onClick={() => setShowImportDialog(true)}
              className="w-full sm:w-auto px-4 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center justify-center gap-2 min-h-[44px] touch-manipulation"
              title="Import subscriptions from CSV/JSON"
            >
              <span className="material-symbols-outlined text-sm">upload</span>
              <span>Import</span>
            </button>
            <AdminColumnToggle
              columns={columnDefinitions}
              visibleColumns={visibleColumns}
              onToggle={handleToggleColumn}
              onToggleAll={handleToggleAllColumns}
            />
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <button
                onClick={handleExportCSV}
                className="w-full sm:w-auto px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 min-h-[44px] touch-manipulation"
                title={selectedRows.length > 0 ? `Export ${selectedRows.length} selected subscriptions` : 'Export all subscriptions'}
              >
                <span className="sm:hidden">CSV</span>
                <span className="hidden sm:inline">Export CSV</span>
                {selectedRows.length > 0 && ` (${selectedRows.length})`}
              </button>
              <button
                onClick={handleExportJSON}
                className="w-full sm:w-auto px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 min-h-[44px] touch-manipulation"
                title={selectedRows.length > 0 ? `Export ${selectedRows.length} selected subscriptions` : 'Export all subscriptions'}
              >
                <span className="sm:hidden">JSON</span>
                <span className="hidden sm:inline">Export JSON</span>
                {selectedRows.length > 0 && ` (${selectedRows.length})`}
              </button>
            </div>
          </div>
        </div>

        {/* Quick Filters */}
        <AdminQuickFilters filters={quickFilters} activeFilter={activeQuickFilter} className="mb-4" />

        {/* Date Range Filter */}
        <AdminDateRangeFilter
          onDateRangeChange={(start, end) => setDateRange({ start, end })}
          className="mb-4"
        />

        {/* Search Bar */}
        <AdminSearchBar
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search by user name or topic..."
        />
      </div>

      <div className="bg-white dark:bg-luna-primary rounded-xl border-2 border-luna-secondary/30 overflow-hidden">
        <div className="overflow-x-auto overscroll-x-contain touch-pan-x -mx-4 sm:mx-0">
          <div className="inline-block min-w-full px-4 sm:px-0">
            <table className="w-full min-w-[500px] sm:min-w-0">
            <thead className="bg-luna-secondary/10">
              <tr>
                {visibleColumns.includes('select') && (
                  <th className="px-3 sm:px-4 py-3 text-left text-xs sm:text-sm font-semibold text-luna-text-light dark:text-luna-text-dark w-12 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedRows.length === subscriptions.length && subscriptions.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedRows(subscriptions.map((sub) => sub.id));
                        } else {
                          setSelectedRows([]);
                        }
                      }}
                      className="rounded border-luna-secondary/30"
                    />
                  </th>
                )}
                {visibleColumns.includes('user') && (
                  <th className="px-3 sm:px-4 py-3 text-left text-xs sm:text-sm font-semibold text-luna-text-light dark:text-luna-text-dark whitespace-nowrap">
                    User
                  </th>
                )}
                {visibleColumns.includes('topic') && (
                  <th className="px-3 sm:px-4 py-3 text-left text-xs sm:text-sm font-semibold text-luna-text-light dark:text-luna-text-dark whitespace-nowrap">
                    Topic
                  </th>
                )}
                {visibleColumns.includes('email_enabled') && (
                  <th className="px-3 sm:px-4 py-3 text-left text-xs sm:text-sm font-semibold text-luna-text-light dark:text-luna-text-dark whitespace-nowrap">
                    Email Enabled
                  </th>
                )}
                {visibleColumns.includes('created_at') && (
                  <th className="px-3 sm:px-4 py-3 text-left text-xs sm:text-sm font-semibold text-luna-text-light dark:text-luna-text-dark whitespace-nowrap">
                    Created
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {subscriptions.length === 0 ? (
                <tr>
                  <td colSpan={visibleColumns.length} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <span className="material-symbols-outlined text-4xl text-luna-text-light/30 dark:text-luna-text-dark/30 mb-4">
                        subscriptions
                      </span>
                      <p className="text-sm font-medium text-luna-text-light dark:text-luna-text-dark mb-2">
                        {searchQuery ? 'No subscriptions found matching your search' : 'No subscriptions found'}
                      </p>
                      <p className="text-xs text-luna-text-light/70 dark:text-luna-text-dark/70">
                        {searchQuery
                          ? 'Try adjusting your search query'
                          : 'Subscriptions will appear here once users subscribe to law topics'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                subscriptions
                  .slice((currentPage - 1) * pageSize, currentPage * pageSize)
                  .map((sub) => (
                <tr
                  key={sub.id}
                  className="border-t border-luna-secondary/10 hover:bg-luna-secondary/5"
                >
                  {visibleColumns.includes('select') && (
                    <td className="px-3 sm:px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedRows.includes(sub.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedRows([...selectedRows, sub.id]);
                          } else {
                            setSelectedRows(selectedRows.filter((id) => id !== sub.id));
                          }
                        }}
                        className="rounded border-luna-secondary/30"
                      />
                    </td>
                  )}
                  {visibleColumns.includes('user') && (
                    <td className="px-3 sm:px-4 py-3 text-xs sm:text-sm text-luna-text-light dark:text-luna-text-dark">
                      {sub.profiles
                        ? `${sub.profiles.first_name || ''} ${sub.profiles.last_name || ''}`.trim() ||
                          'Unknown'
                        : 'Unknown'}
                    </td>
                  )}
                  {visibleColumns.includes('topic') && (
                    <td className="px-3 sm:px-4 py-3 text-xs sm:text-sm text-luna-text-light dark:text-luna-text-dark">
                      {sub.law_topics?.name || 'Unknown'}
                    </td>
                  )}
                  {visibleColumns.includes('email_enabled') && (
                    <td className="px-3 sm:px-4 py-3 text-xs sm:text-sm">
                      <button
                        onClick={() => handleToggleEmailEnabled(sub.id, sub.email_enabled)}
                        disabled={updatingEmailEnabled === sub.id}
                        className={`px-4 py-2.5 sm:px-3 sm:py-1.5 rounded-lg text-sm sm:text-xs font-medium transition-colors min-h-[44px] sm:min-h-[32px] touch-manipulation disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto ${
                          sub.email_enabled
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50'
                            : 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-900/50'
                        }`}
                        aria-label={`${sub.email_enabled ? 'Disable' : 'Enable'} email notifications for this subscription`}
                        aria-busy={updatingEmailEnabled === sub.id}
                      >
                        {updatingEmailEnabled === sub.id ? (
                          <span className="flex items-center gap-1">
                            <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></span>
                            Updating...
                          </span>
                        ) : (
                          sub.email_enabled ? 'Enabled' : 'Disabled'
                        )}
                      </button>
                    </td>
                  )}
                  {visibleColumns.includes('created_at') && (
                    <td className="px-3 sm:px-4 py-3 text-xs sm:text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
                      {sub.created_at ? (() => {
                        try {
                          const date = new Date(sub.created_at);
                          if (isNaN(date.getTime())) return 'Invalid date';
                          return format(date, 'MMM dd, yyyy');
                        } catch (e) {
                          return 'Invalid date';
                        }
                      })() : 'N/A'}
                    </td>
                  )}
                </tr>
                  ))
              )}
            </tbody>
          </table>
          </div>
        </div>

        {/* Pagination */}
        {subscriptions.length > 0 && (
          <AdminPagination
            currentPage={currentPage}
            totalPages={Math.ceil(subscriptions.length / pageSize)}
            onPageChange={setCurrentPage}
            pageSize={pageSize}
            totalItems={subscriptions.length}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
          />
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AdminConfirmDialog
        isOpen={showDeleteDialog}
        title="Delete Subscriptions"
        message={`Are you sure you want to delete ${selectedRows.length} subscription(s)? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmBulkDelete}
        onCancel={() => setShowDeleteDialog(false)}
        variant="danger"
      />

      {/* Import Dialog */}
      <AdminImportDialog
        isOpen={showImportDialog}
        onClose={() => setShowImportDialog(false)}
        onImport={handleImport}
        acceptedFormats={['.csv', '.json']}
        sampleData={sampleImportData}
        title="Import Subscriptions"
      />
    </div>
  );
};

